import { NODE_BASE_URL } from '../api'

export type AgentStreamEvent =
    | { type: 'token'; content: string }
    | { type: 'tool_call'; id: string; name: string; args: Record<string, unknown> }
    | { type: 'tool_result'; id: string; name: string; success: boolean; result: unknown; durationMs: number }
    | { type: 'done'; model: string; conversationId: string }
    | { type: 'title_updated'; title: string }
    | { type: 'error'; message: string }

export type ToolCallRecord = {
    id: string
    name: string
    arguments?: Record<string, unknown>
    precedingText: string
    result: unknown
    success: boolean
    durationMs: number
}

export type AgentMessage = {
    role: 'user' | 'assistant'
    content: string
    toolCalls?: ToolCallRecord[]
    attachments?: AgentAttachment[]
}

export type AgentAttachment =
    | {
          kind: 'document'
          id: string
          name?: string
          dataType?: string | null
          url?: string | null
      }
    | {
          kind: 'foundation_run'
          id: string
          name?: string
      }
    | {
          /** Creative / Communication Brief (includes linked Foundation profile). */
          kind: 'communication_brief'
          id: string
          name?: string
          foundationRunId?: string
          subjectName?: string
      }
    | {
          kind: 'heft_profile'
          id: string
          subjectType: 'person' | 'company' | 'product'
          name?: string
          /** Bio slice key (code: lens). */
          lens?: string
          /** Persona id (code: view id). */
          viewId?: string
          /** Optional UI labels for display. */
          lensLabel?: string
          viewName?: string
      }
    | {
          kind: 'heft_story'
          id: string
          title?: string
          subjectType?: 'person' | 'company' | 'product'
          subjectName?: string
          storyStrength?: 'high' | 'medium' | 'low'
          storyTags?: string[]
          createdAt?: string
      }

export type AgentConversationScope = 'general' | 'deliverable_studio' | 'brief_strategist'

export type AgentConversation = {
    id: string
    title: string
    lastMessageAt: string
    createdAt: string
    scope?: AgentConversationScope
    studioDraftDocId?: string | null
}

export type AgentPageContextPayload = {
    label: string
    entity?: string
    id?: string
    ref?: string
    metadata?: Record<string, unknown>
}

type StreamChatPayload = {
    message: string
    conversationId?: string
    conversationScope?: AgentConversationScope
    studioDraftDocId?: string
    history?: AgentMessage[]
    model?: string
    pageContext?: AgentPageContextPayload
    attachments?: AgentAttachment[]
}

class AgentService {
    async streamChat(
        payload: StreamChatPayload,
        onEvent: (event: AgentStreamEvent) => void,
        signal?: AbortSignal
    ): Promise<void> {
        const response = await fetch(`${NODE_BASE_URL}/agent/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload),
            signal,
        })

        if (!response.ok) {
            throw new Error(`Stream request failed with status ${response.status}`)
        }

        if (!response.body) {
            throw new Error('No stream body returned by server')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let isDone = false

        try {
            while (!isDone) {
                const { done, value } = await reader.read()
                if (done) {
                    isDone = true
                    continue
                }

                buffer += decoder.decode(value, { stream: true })

                const blocks = buffer.split('\n\n')
                buffer = blocks.pop() || ''

                for (const block of blocks) {
                    const lines = block.split('\n')
                    for (const line of lines) {
                        if (!line.startsWith('data:')) continue
                        const raw = line.slice(5).trim()
                        if (!raw) continue
                        try {
                            const parsed = JSON.parse(raw) as AgentStreamEvent
                            onEvent(parsed)
                        } catch {
                            // Ignore malformed chunks
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock()
        }
    }
}

const agentService = new AgentService()

export async function fetchConversations(
    limit = 20,
    scope: AgentConversationScope = 'general',
    studioDraftDocId?: string | null
): Promise<{ conversations: AgentConversation[] }> {
    const params = new URLSearchParams({ limit: String(limit), scope })
    if (scope === 'deliverable_studio' && studioDraftDocId) {
        params.set('studioDraftDocId', studioDraftDocId)
    }
    const response = await fetch(`${NODE_BASE_URL}/agent/conversations?${params.toString()}`, {
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error('Failed to fetch conversations')
    }
    return response.json()
}

export async function fetchConversation(
    id: string
): Promise<{ conversation: AgentConversation; messages: AgentMessage[] }> {
    const response = await fetch(`${NODE_BASE_URL}/agent/conversations/${id}`, {
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error('Failed to fetch conversation')
    }
    return response.json()
}

export async function deleteConversation(id: string): Promise<void> {
    const response = await fetch(`${NODE_BASE_URL}/agent/conversations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error('Failed to delete conversation')
    }
}

export default agentService
